/**
 * AppFrame — 인증 영역 공통 프레임 (Shell)
 *
 * 소유: 프레임 팀. 에이전트 담당 그룹은 본 디렉터리(app/shell/)를 수정하지 않는다.
 * 에이전트는 registry(AgentManifest)로만 메뉴·라우트를 등록하며,
 * 중앙 컨텐츠는 <Outlet/> 에 렌더된다. 좌측 내비·상단 바는 본 프레임이 고정 소유한다.
 * 우측 현장 패널(SiteArea)은 제거됨 — 고객현장은 /sites 전용 화면으로 대체(SitesListPage).
 *
 * 알림: NotificationProvider 가 프레임 전체를 감싸 SSE 수신·배지·토스트를 전담한다(F-03 §7.6).
 *
 * 초기화: 프레임 마운트 시 GET /indipia/init + GET /member/auth/session 을 병렬 호출해
 * useAppStore(공통코드·구독·세션)에 주입한다. READ 전용 — 수정 불가.
 *
 * 레이아웃:
 *   ┌──────────┬──────────────────────────────────────┐
 *   │          │  TopBar (full-width, 우측 끝까지)     │
 *   │ LeftNav  ├──────────────────────────────────────┤
 *   │          │  Outlet (중앙)                        │
 *   └──────────┴──────────────────────────────────────┘
 */
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchInit, fetchSession, useAppStore } from '@indipia/shared';
import { ScrollTopButton } from '@indipia/ui-core';
import { LeftNav } from './LeftNav';
import { TopBar } from './TopBar';
import { SitePanelProvider } from './SitePanelContext';
import { NotificationProvider } from './notifications/NotificationProvider';
import { ToastViewport } from './notifications/ToastViewport';
import { InfoToastViewport } from './notifications/InfoToast';
import { PartnerReviewHost } from './PartnerReviewHost';

function useAppInit() {
  const setInit    = useAppStore((s) => s.setInit);
  const setSession = useAppStore((s) => s.setSession);

  const { data: initData } = useQuery({
    queryKey: ['app-init'],
    queryFn: fetchInit,
    staleTime: Infinity,
    retry: 1,
  });

  const { data: sessionData } = useQuery({
    queryKey: ['app-session'],
    queryFn: fetchSession,
    staleTime: Infinity,
    retry: 1,
  });

  useEffect(() => { if (initData)    setInit(initData);       }, [initData,    setInit]);
  useEffect(() => { if (sessionData) setSession(sessionData); }, [sessionData, setSession]);
}

// 직원 전용 경로 — 리다이렉트 루프 방지
const EMPLOYEE_ONLY_PATHS = ['/app/no-company', '/app/biz-check', '/app/welcome'];
// 소속 대기(PENDING)·소속 없음(REJECTED·RELEASED·미신청) 직원이 LNB(프로필·고객센터·알림)에서 자유롭게 이동 가능한 경로
const RESTRICTED_STAFF_ALLOWED_PATHS = ['/settings/', '/cs/', '/notifications'];

/**
 * ContentGuard — Outlet 자리에서만 리다이렉트 처리
 * AppFrame 전체를 return하지 않으므로 LeftNav 등 Shell 레이아웃은 마운트 유지.
 */
function ContentGuard() {
  const { pathname } = useLocation();
  const session      = useAppStore((s) => s.session);
  const initLoaded   = useAppStore((s) => s.initLoaded);
  const studioStage  = useAppStore((s) => s.studioStage);

  // session 미로드 시 빈 화면 대기 — 홈이 flash되는 현상 방지
  if (!session) return null;

  if (session.masterYn === 'Y') {
    // init 미완료 시 studioStage 확정 불가 → 대기
    if (!initLoaded) return null;
    // 마스터 신규: studioStage === 0 → B-1 사장님 웰컴
    if (studioStage === 0) return <Navigate to="/master-welcome" replace />;
  }

  // 직원 전용 경로는 아래 리다이렉트에서 제외
  const isEmployeePath = EMPLOYEE_ONLY_PATHS.some((p) => pathname.startsWith(p));
  if (session.masterYn === 'N' && !isEmployeePath) {
    // 프로필·고객센터·알림은 소속 대기·소속 없음 상태에서도 자유 이동 허용
    const isRestrictedAllowed = RESTRICTED_STAFF_ALLOWED_PATHS.some((p) => pathname.startsWith(p));
    if (!isRestrictedAllowed) {
      if (session.affiliationStatus === 'PENDING') {
        return <Navigate to="/app/welcome" replace />;
      }
      if (!session.compId) {
        return <Navigate to="/app/no-company" replace />;
      }
    }
  }

  return <Outlet />;
}

export function AppFrame() {
  useAppInit();

  return (
    <NotificationProvider>
      <SitePanelProvider>
        <PartnerReviewHost>
        <div className="flex h-screen overflow-hidden bg-surface text-text">
          <LeftNav />
          <div className="flex min-w-0 flex-1 flex-col">
            <TopBar />
            <div className="flex min-h-0 flex-1">
              <main className="min-w-0 flex-1 overflow-auto bg-surface">
                <ContentGuard />
                {/* 스크롤 맨 위로 (§15.55) — main 이 doc 스크롤 소유자다(D-07 §21.4).
                    main 직속 자식이라 버튼이 스스로 main 을 스크롤 소유자로 찾는다.
                    짧은 화면은 scrollTop 이 threshold(400)를 못 넘어 저절로 숨는다 — 화면별 분기가 필요 없다.
                    fixed 스크롤 화면(.page-scroll, §15.42)은 main 이 스크롤하지 않아 여기 버튼이 뜨지 않는다 — 그쪽은 PageScroll 이 자기 것을 갖는다. */}
                <ScrollTopButton />
              </main>
            </div>
          </div>
        </div>
        <ToastViewport />
        <InfoToastViewport />
        </PartnerReviewHost>
      </SitePanelProvider>
    </NotificationProvider>
  );
}
